import React, { useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { AdPageTestIds } from '@/__e2e__/testIds/ad-page';
import { useAdDetails } from '@/hooks/useAdDetails';
import { PayForAdByPrepaidBundleResponse } from '@/api/types/ads.types';
import { PAY_FOR_AD_BY_PREPAID_BUNDLE } from '@/api/mutations/payments.mutations';
import { BACKEND_ERRORS } from '@/constants/backendErrors';
import { useCurrentCategoryDetails } from '@/hooks/useCurrentCategory/useCurrentCategoryDetails';
import { WarningModal } from '@/components/Ad/WarningModal';
import { Button } from '@/components/UI/Button/Button';
import { CommonTestIds } from '@/__e2e__/testIds/common';

type Props = {
  dataTestId?: string;
  paymentFlowToggle: () => void;
};

export type PayByBalancePopupRef = {
  toggle: () => void;
};

export const PayByBalancePopup = React.forwardRef<PayByBalancePopupRef, Props>(
  ({ dataTestId, paymentFlowToggle }, ref) => {
    const { t } = useTranslation();
    const [isPopupOpen, togglePopup] = useToggleOpenState();
    const { categoryDetails } = useCurrentCategoryDetails();
    const [isLoading, setIsLoading] = useState(false);

    const { adDetails, refetch: refetchAdDetails } = useAdDetails();
    const [payForAdByPrepaidBundle] =
      useMutation<PayForAdByPrepaidBundleResponse>(
        PAY_FOR_AD_BY_PREPAID_BUNDLE,
        {
          onCompleted: onPayForAdByPrepaidBundleComplete,
        }
      );

    const description = t('pages.ad.pay_by_balance_description', {
      categoryName: categoryDetails?.name,
    });

    useImperativeHandle(
      ref,
      () => ({
        toggle: togglePopup,
      }),
      []
    );

    function onPayForAdByPrepaidBundleComplete(
      data: PayForAdByPrepaidBundleResponse
    ) {
      const isNoSpendableItemsError = data.payForAdByPrepaidBundle.errors?.some(
        error => error.message === BACKEND_ERRORS.NO_SPENDABLE_BUNDLE_ITEMS
      );

      if (isNoSpendableItemsError) {
        refetchAdDetails();
        togglePopup(false);
        paymentFlowToggle();
        setIsLoading(false);
        return;
      }

      refetchAdDetails().then(() => {
        if (data.payForAdByPrepaidBundle.entity) {
          togglePopup(false);
          setIsLoading(false);
        }
      });
    }

    function onConfirmClick() {
      if (adDetails) {
        setIsLoading(true);
        payForAdByPrepaidBundle({ variables: { adId: adDetails.id } });
      }
    }

    return (
      <WarningModal
        dataTestId={dataTestId ?? AdPageTestIds.popup.payByBalance}
        isOpen={isPopupOpen}
        onClose={() => togglePopup(false)}
        title={t('pages.ad.pay_by_balance')}
        description={description}
        actions={
          <>
            <Button
              data-testid={CommonTestIds.popup.actionLeft}
              appearance="secondaryOutlined"
              isFullWidth
              borderRadius={14}
              disabled={isLoading}
              onClick={() => togglePopup(false)}
            >
              {t('common.deny')}
            </Button>
            <Button
              data-testid={CommonTestIds.popup.actionRight}
              appearance="secondary"
              isFullWidth
              borderRadius={14}
              disabled={isLoading}
              onClick={onConfirmClick}
            >
              {t('common.confirm')}
            </Button>
          </>
        }
      />
    );
  }
);

PayByBalancePopup.displayName = 'PayByBalancePopup';
